import React from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { Framerate } from '../models/Timeline';

interface TickmarksProps {
  duration: number;
  timelineScale: number;
  width: number;
  trackLabelWidth?: number; // Optional track label width to adjust calculations
  fps?: Framerate; // Frame rate
  showFrames?: boolean; // Whether to show frames
  onClick?: (time: number) => void; // Optional click handler to set playhead position
}

export const Tickmarks = observer(({ 
  duration, 
  timelineScale, 
  width, 
  trackLabelWidth = 0, 
  fps = Framerate.FPS_30,
  showFrames = false,
  onClick
}: TickmarksProps) => {
  // Create tickmarks directly without useMemo
  const marks = [];
  const secondWidth = timelineScale;
  const tickCount = Math.ceil(Math.max(duration, width / secondWidth));
  
  // Determine the appropriate interval based on zoom level
  let interval = 1; // 1 second
  if (secondWidth < 20) interval = 10;
  else if (secondWidth < 50) interval = 5;
  else if (secondWidth < 100) interval = 2;
  
  // Add second markers
  for (let i = 0; i <= tickCount; i += interval) {
    const position = i * secondWidth;
    if (position > width) break;
    
    marks.push(
      <Tick key={i} style={{ left: `${position}px` }}>
        <TickLine />
        <TickLabel>{formatTime(i, fps, showFrames)}</TickLabel>
      </Tick>
    );
  }
  
  // Add frame markers if zoomed in enough and showFrames is true
  if (showFrames && secondWidth > 100) {
    // Only show frame markers when we have enough space
    const frameInterval = Math.max(1, Math.floor(fps / 10)); // Show every N frames based on FPS
    const frameWidth = secondWidth / fps;
    
    for (let i = 0; i <= tickCount * fps; i += frameInterval) {
      if (i % fps === 0) continue; // Skip second markers (already added)
      
      const position = i * frameWidth;
      if (position > width) break;
      
      marks.push(
        <FrameTick key={`frame-${i}`} style={{ left: `${position}px` }}>
          <FrameTickLine />
        </FrameTick>
      );
    }
  }

  // Handle click on tickmarks to set playhead position
  const handleTickmarksClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onClick) return;
    
    // The tickmarks container already excludes the label area in the new layout
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    
    const time = clickX / timelineScale;
    const clampedTime = Math.max(0, Math.min(time, duration));
    
    onClick(clampedTime);
  };

  return (
    <TickmarksContainer onClick={onClick ? handleTickmarksClick : undefined}>
      {marks}
    </TickmarksContainer>
  );
});

// Format time as MM:SS or MM:SS:FF (frames)
const formatTime = (seconds: number, fps: Framerate = Framerate.FPS_30, showFrames: boolean = false): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  
  if (showFrames) {
    // Calculate the frame at this exact second
    const frame = 0; // At exact second boundaries, frame is 0
    return `${mins}:${secs.toString().padStart(2, '0')}:${frame.toString().padStart(2, '0')}`;
  } else {
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
};

const TickmarksContainer = styled.div`
  position: relative;
  height: 24px;
  width: 100%;
  background: #171724;
  border-bottom: 1px solid #333;
  cursor: pointer;
  
  &:hover {
    background: #1a1a2a; /* Slightly lighter on hover to indicate clickable */
  }
`;

const Tick = styled.div`
  position: absolute;
  height: 100%;
  width: 1px;
`;

const TickLine = styled.div`
  position: absolute;
  bottom: 0;
  width: 1px;
  height: 8px;
  background-color: #666;
`;

const TickLabel = styled.div`
  position: absolute;
  bottom: 8px;
  left: 4px;
  font-size: 10px;
  color: #999;
  white-space: nowrap;
`;

const FrameTick = styled.div`
  position: absolute;
  height: 100%;
  width: 1px;
`;

const FrameTickLine = styled.div`
  position: absolute;
  bottom: 0;
  width: 1px;
  height: 4px;
  background-color: #444;
`;